import { useColorMode, useEventListener } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { isJWTValid, parseJWT } from '@/libs/auth'
import i18n from '@/libs/i18n'

/** The colour theme of this browser: the company theme, or the colours of the first ZeroNMS web
 * (`src/theme/classic.css`). Layout and components are the same in both. */
export type UITheme = 'company' | 'classic'

const UI_THEME_KEY = 'uiTheme'

const applyUITheme = (theme: UITheme) => {
    if (theme === 'company') {
        delete document.documentElement.dataset.theme
    } else {
        document.documentElement.dataset.theme = theme
    }
}

export interface LoggedInUserData {
    id:       string
    name:     string
    username: string
    role?:    string
}

export const useAppStore = defineStore('app', () => {
    const JWT          = ref(localStorage.getItem('JWT') || '')
    const locale       = ref(localStorage.getItem('locale') || 'en')
    const windowWidth  = ref(window.innerWidth)
    const loaderActive = ref(0)

    const userData = computed((): LoggedInUserData | undefined => {
        const claims = parseJWT(JWT.value)
        return claims ? {
            id:       claims.id,
            name:     claims.name || claims.username,
            username: claims.username,
            role:     claims.role,
        } : undefined
    })

    const colorMode = useColorMode({ storageKey: 'vueuse-color-scheme' })

    const uiTheme = ref<UITheme>(localStorage.getItem(UI_THEME_KEY) === 'classic' ? 'classic' : 'company')
    applyUITheme(uiTheme.value)

    const setUITheme = (theme: UITheme) => {
        uiTheme.value = theme
        localStorage.setItem(UI_THEME_KEY, theme)
        applyUITheme(theme)
    }

    useEventListener(window, 'resize', () => { windowWidth.value = window.innerWidth })

    const getJWT         = () => JWT.value
    const isLoggedIn     = () => isJWTValid(JWT.value)
    const isLoaderActive = () => loaderActive.value > 0
    const isMobile       = () => windowWidth.value < 1024

    const setJWTCookie = (jwt: string) => {
        if (jwt) {
            document.cookie = `JWT=${jwt}; path=/; SameSite=Lax`
        } else {
            document.cookie = 'JWT=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax'
        }
    }

    setJWTCookie(JWT.value)

    const setLogin = (jwt: string) => {
        localStorage.setItem('JWT', jwt)
        JWT.value = jwt
        setJWTCookie(jwt)
    }

    const setLogout = () => {
        localStorage.removeItem('JWT')
        JWT.value = ''
        setJWTCookie('')
    }

    const startLoading = () => loaderActive.value++
    const stopLoading  = () => { loaderActive.value = Math.max(0, loaderActive.value - 1) }

    const changeLocale = (newLocale: string) => {
        locale.value = newLocale
        localStorage.setItem('locale', newLocale)
        ;(i18n.global.locale as any).value = newLocale
    }

    return {
        JWT,
        locale,
        userData,
        loaderActive,
        colorMode,
        uiTheme,
        setUITheme,
        getJWT,
        isLoggedIn,
        isLoaderActive,
        isMobile,
        setLogin,
        setLogout,
        startLoading,
        stopLoading,
        changeLocale,
    }
})
